import { useEffect, useRef } from "react";
import "swiper/css";
import "./ComparisonSection.css";

const FEATURE_ICONS = {
  location: "📍",
  rating: "⭐",
  pricePlan: "💰",
  structure: "🏗️",
  reraApproved: "✅",
  amenities: "🏊",
  layout: "📐",
  sizeSqft: "📏",
  roiPercent: "📈",
};

const FEATURE_LABELS = {
  location: "Location",
  rating: "Rating",
  pricePlan: "Price Plan",
  structure: "Structure",
  reraApproved: "RERA",
  amenities: "Amenities",
  layout: "Layout",
  sizeSqft: "Size",
  roiPercent: "ROI",
};

export default function ComparisonSection({
  comparisons = [],
  projectName = "Project",
}) {
  const swiperRef = useRef(null);
  const swiperInstanceRef = useRef(null);

  // ✅ helper → hide unwanted values
  const shouldHide = (val) =>
    val === undefined || val === null || val === "" || val === "N/A";

  // ✅ SWIPER INIT
  useEffect(() => {
    if (!comparisons.length) return;

    import("swiper").then((mod) => {
      const Swiper = mod.default || mod.Swiper;

      if (swiperRef.current && Swiper) {
        // destroy old instance
        if (swiperInstanceRef.current) {
          swiperInstanceRef.current.destroy(true, true);
        }

        swiperInstanceRef.current = new Swiper(swiperRef.current, {
          slidesPerView: 1,
          spaceBetween: 20,
          loop: true,
          autoplay: {
            delay: 3500,
            disableOnInteraction: false,
          },
          breakpoints: {
            480: { slidesPerView: 1 },
            768: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
          },
        });
      }
    });

    return () => {
      if (swiperInstanceRef.current) {
        swiperInstanceRef.current.destroy(true, true);
      }
    };
  }, [comparisons]);

  // ❌ no data
  if (!comparisons.length) return null;

  // ✅ value formatter
  const formatValue = (key, val) => {
    let display = val;

    if (key === "rating") display = `${val}/5`;
    if (key === "roiPercent") display = `${val}%`;
    if (key === "sizeSqft") display = `${val} sq.ft`;

    return display;
  };

  return (
    <section className="comparison-section section">
      <div className="container">
        <h2 className="section-heading">
          {projectName} Comparisons
        </h2>
      </div>

      <div className="swiper comparison-swiper" ref={swiperRef}>
        <div className="swiper-wrapper">
          {comparisons.map((proj, i) => (
            <div key={i} className="swiper-slide">
              <article className="comparison-card">

                <h3 className="comparison-card__title">
                  {proj.name || "Project"}
                </h3>

                <ul className="comparison-card__features">
                  {Object.keys(FEATURE_ICONS).map((key) => {
                    const val = proj?.[key];

                    // ✅ hide empty / N/A fields
                    if (shouldHide(val)) return null;

                    const icon = FEATURE_ICONS[key];
                    const display = formatValue(key, val);

                    return (
                      <li key={key} className="comparison-card__feature">
                        <span className="comparison-card__icon">
                          {icon}
                        </span>

                        <span className="comparison-card__label">
                          {FEATURE_LABELS[key]}:
                        </span>

                        <span className="comparison-card__value">
                          {display}
                        </span>
                      </li>
                    );
                  })}
                </ul>

              </article>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}